import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injector, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReportingControllerService } from '@api/client';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { DateTimeService } from '@shared/services/datetime/datetime.service';
import { DownloadStateService } from '@shared/services/download-state.service';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { toApiTimezone } from '@shared/pipes/translate-timezone.pipe';
import { toUTCAPIDateTimeString } from '@shared/utils/datetime';
import { saveAs } from 'file-saver';
import { omit } from 'lodash';
import { Observable, mergeMap, pipe, switchMap } from 'rxjs';
import { PendingDownloads, ReportFileFormat, ReportType } from '../dashboard.interfaces';
import { getFileName } from '../dashboard.utils';

export const DOWNLOAD_REPORT_KEY = 'report';

export const useReports = () =>
  signalStoreFeature(
    {
      props: {
        _activeAccountId: type<Signal<number | undefined>>(),
        _injector: type<Injector>(),
        _dialogService: type<MatDialog>(),
        _notificationService: type<NotificationService>(),
        _dateTimeService: type<DateTimeService>(),
        _downloadStateService: type<DownloadStateService>(),
      },
    },
    withState({
      pendingDownloads: {} as PendingDownloads,
    }),
    withProps(() => ({
      _reportsService: inject(ReportingControllerService),
    })),
    withMethods(store => {
      const _addPendingDownload = (id: string, fileName: string) =>
        patchState(store, state => ({
          pendingDownloads: { ...state.pendingDownloads, [id]: { fileName } },
        }));

      const _requestReport = (
        request$: Observable<{ id?: number }>,
        fileName: string,
        errorMessage: string,
      ) => {
        store._downloadStateService.start(DOWNLOAD_REPORT_KEY);

        return request$.pipe(
          tapResponse({
            next: ({ id }) => _addPendingDownload(id!.toString(), fileName),
            error: (error: HttpErrorResponse) => {
              store._downloadStateService.error(DOWNLOAD_REPORT_KEY, error);
              store._notificationService.showErrorNotification(errorMessage, error);
            },
          }),
        );
      };

      const downloadOrdersReport = rxMethod<{ fromTime: string; toTime: string }>(
        pipe(
          switchMap(({ fromTime, toTime }) =>
            _requestReport(
              store._reportsService.ordersReport(
                store._activeAccountId(),
                toUTCAPIDateTimeString(fromTime, store._dateTimeService.timezone),
                toUTCAPIDateTimeString(toTime, store._dateTimeService.timezone),
                toApiTimezone(store._dateTimeService.timezone),
              ),
              'orders-report',
              'NOTIFICATION.THE_ORDERS_REPORT_COULDNT_BE_DOWNLOADED',
            ),
          ),
        ),
      );

      const downloadTransfersReport = rxMethod<{ fromTime: string; toTime: string }>(
        pipe(
          switchMap(({ fromTime, toTime }) =>
            _requestReport(
              store._reportsService.transfersReport(
                store._activeAccountId(),
                toUTCAPIDateTimeString(fromTime, store._dateTimeService.timezone),
                toUTCAPIDateTimeString(toTime, store._dateTimeService.timezone),
                toApiTimezone(store._dateTimeService.timezone),
              ),
              'transfers-report',
              'NOTIFICATION.THE_TRANSFERS_REPORT_COULDNT_BE_DOWNLOADED',
            ),
          ),
        ),
      );

      const downloadBalanceReport = rxMethod<{
        pointInTime: string;
        fileFormat?: ReportFileFormat;
      }>(
        pipe(
          switchMap(({ pointInTime, fileFormat }) =>
            _requestReport(
              store._reportsService.balanceReport(
                toUTCAPIDateTimeString(pointInTime, store._dateTimeService.timezone),
                store._activeAccountId(),
                fileFormat === ReportFileFormat.PDF,
                toApiTimezone(store._dateTimeService.timezone),
              ),
              getFileName('balance-report', fileFormat),
              'NOTIFICATION.THE_BALANCE_REPORT_COULDNT_BE_DOWNLOADED',
            ),
          ),
        ),
      );

      const downloadComprehensiveReport = rxMethod<{ fromTime: string; toTime: string }>(
        pipe(
          switchMap(({ fromTime, toTime }) =>
            _requestReport(
              store._reportsService.comprehensiveReport(
                toUTCAPIDateTimeString(fromTime, store._dateTimeService.timezone),
                toUTCAPIDateTimeString(toTime, store._dateTimeService.timezone),
                store._activeAccountId(),
                toApiTimezone(store._dateTimeService.timezone),
              ),
              'comprehensive-report.pdf',
              'NOTIFICATION.THE_COMPREHENSIVE_REPORT_COULDNT_BE_DOWNLOADED',
            ),
          ),
        ),
      );

      const downloadPendingReport = rxMethod<string>(
        pipe(
          mergeMap(id => {
            const fileName = store.pendingDownloads()[id]?.fileName;

            return store._reportsService.download(Number(id)).pipe(
              tapResponse({
                next: data => {
                  saveAs(data, fileName);
                  patchState(store, state => ({
                    pendingDownloads: omit(state.pendingDownloads, id),
                  }));
                  store._downloadStateService.success(DOWNLOAD_REPORT_KEY);
                },
                error: (error: HttpErrorResponse) => {
                  store._downloadStateService.error(DOWNLOAD_REPORT_KEY, error);
                  store._notificationService.showErrorNotification(
                    `The report couldn't be downloaded`,
                    error,
                  );
                },
              }),
            );
          }),
        ),
      );

      const reportFailed = () => {
        store._downloadStateService.error(DOWNLOAD_REPORT_KEY);
        store._notificationService.showErrorNotification(
          'NOTIFICATION.NO_DATA_FOR_REPORT',
        );
      };

      const openDownloadReportDialog = async (reportType: ReportType) => {
        const { DownloadReportDialogComponent } =
          await import('app/dashboard/components/download-report-dialog/download-report-dialog.component');

        store._dialogService.open(DownloadReportDialogComponent, {
          data: reportType,
          injector: store._injector,
        });
      };

      return {
        downloadOrdersReport,
        downloadTransfersReport,
        downloadBalanceReport,
        downloadComprehensiveReport,
        downloadPendingReport,
        reportFailed,
        openDownloadReportDialog,
      };
    }),
  );
